<script setup lang="ts">
import dayjs from 'dayjs'
import locale_fr from 'dayjs/locale/fr'
import localizedFormat from 'dayjs/plugin/localizedFormat'

const route = useRoute()
const config = useRuntimeConfig()
const baseStore = useBaseStore()
const { locale } = useI18n()

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: '/article/[id]/[slug]',
  },
})

definePageMeta({
  name: 'ArticlePage',
})

// Setup dayjs
if (locale.value === 'fr') {
  dayjs.locale(locale_fr)
}
dayjs.extend(localizedFormat)

// Fetch article data
async function fetchArticle(id: string) {
  return await $fetch(`${config.public.apiUrl}/blogs/fetch-article`, {
    query: { id },
  })
}

// SSR + hydration
const { data: articleData, pending, refresh } = await useAsyncData(
  () => `article-${route.params.id}`,
  () => fetchArticle(route.params.id as string),
  {
    default: () => ({
      article: null,
      recent_articles: [],
      related_articles: [],
    } as any),
  },
)

// Reactive data
const article = computed(() => articleData.value?.article || null)
const recentArticles = computed(() => articleData.value?.recent_articles || [])
const relatedArticles = computed(() => articleData.value?.related_articles || [])

useHead({
  title: computed(() => article.value ? `${article.value.title} - Glouton` : 'Article - Glouton'),
  meta: [
    { name: 'description', content: computed(() => article.value?.preview || 'Découvrez cet article sur Glouton') },
    { property: 'og:title', content: computed(() => article.value ? `${article.value.title} - Glouton` : 'Article - Glouton') },
    { property: 'og:description', content: computed(() => article.value?.preview || 'Découvrez cet article sur Glouton') },
    { property: 'og:url', content: computed(() => `${config.public.domain}${route.path}`) },
    { property: 'og:image', content: computed(() => article.value?.thumbnailUrl ? `${config.public.cdnUrl}${article.value.thumbnailUrl}` : 'https://glouton.b-cdn.net/site/images/social.jpg') },
  ],
})

// Computed properties
const formattedDate = computed(() => {
  if (!article.value?.published_date)
    return ''
  return dayjs(article.value.published_date).format('LL')
})

// Handle internal link clicks
function startLinkListener() {
  if (typeof document !== 'undefined') {
    document.querySelectorAll('.single-article a').forEach((link) => {
      link.removeEventListener('click', linkAction)
      link.addEventListener('click', linkAction)
    })
  }
}

function linkAction(e: Event) {
  const target = e.target as HTMLAnchorElement
  if (target.href.includes('https://www.glouton.app/fr') && !target.href.includes('#')) {
    e.preventDefault()
    navigateTo(target.href.replace('https://www.glouton.app/fr', ''))
  }
}

// Initialize
onMounted(() => {
  // Scroll to top
  document.querySelector('body')?.scrollIntoView({ behavior: 'smooth' })

  // Start link listener after content is loaded
  nextTick(() => {
    startLinkListener()
  })
})

// Watch for article changes to start link listener
watch(article, () => {
  if (article.value) {
    nextTick(() => {
      startLinkListener()
      if (import.meta.client) {
        baseStore.scheduleMadopsReset()
      }
    })
  }
}, { immediate: true })
</script>

<template>
  <div id="ArticlePage">
    <UContainer id="ArticlePage-anchor">
      <div v-if="pending" class="flex justify-center items-center py-12">
        <UIcon name="i-mdi-loading" class="animate-spin text-venice text-2xl" />
      </div>

      <div v-else-if="article" class="py-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <!-- Main Article Content -->
        <div class="col-span-1 md:col-span-2">
          <div class="bg-white rounded-lg">
            <!-- Article Header -->
            <div class="relative">
              <img
                class="flex-shrink-0 w-full mx-auto rounded-t-lg"
                :src="`${config.public.backendCdnUrl}${article.thumbnailUrl}`"
                :alt="article.title"
              >
            </div>

            <div class="px-5 pt-5">
              <div>
                <span class="block text-base text-center text-tomate-400 font-semibold tracking-wide uppercase">
                  {{ article.blog?.title }}
                </span>
              </div>
              <h1 class="mt-2 block text-2xl text-center leading-8 font-bold text-poivre-800 sm:text-3xl font-title tracking-wide">
                {{ article.title }}
              </h1>
              <div class="text-center text-poivre-400 mt-1">
                <time :datetime="article.published_date">
                  {{ formattedDate }}
                </time>
                <span aria-hidden="true">&middot;</span>
                <span>{{ article.reading_time }} min de lecture</span>
              </div>
            </div>

            <!-- Article Content -->
            <div class="single-article p-5 text-base text-poivre-700" v-html="article.text" />

            <!-- Share Section -->
            <div class="p-4 m-5 border-2 border-dashed border-poivre-200 rounded-lg">
              <div class="text-center text-xl mb-2 font-semibold text-poivre-800">
                Partagez cet article
              </div>
              <Share
                :url="`${config.public.domain}${route.path}`"
                :title="article.title"
                :description="article.preview"
                :media="`${config.public.backendCdnUrl}${article.thumbnailUrl}`"
              />
            </div>

            <!-- Author Section -->
            <div class="p-5 flex items-center border-t border-poivre-100">
              <div class="flex-shrink-0">
                <img
                  class="h-10 w-10 rounded-full"
                  :src="`${config.public.backendCdnUrl}${article.authorUrl}`"
                  :alt="article.authorName"
                >
              </div>
              <div class="ml-3">
                <p class="text-base font-medium text-poivre-800">
                  {{ article.authorName }}
                </p>
                <div class="text-sm text-poivre-500" v-html="article.authorBio" />
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="col-span-1">
          <!-- Related Articles -->
          <div class="pb-2 mb-2 border-b border-poivre-200">
            <h3 class="text-2xl leading-6 text-poivre-800 font-title">
              Articles reliés
            </h3>
          </div>

          <div v-if="relatedArticles.length > 0" class="space-y-3">
            <NuxtLink
              v-for="relatedArticle in relatedArticles"
              :key="relatedArticle.id"
              :to="$localePath({ name: 'ArticlePage', params: { id: relatedArticle.id, slug: relatedArticle.slug } })"
              class="flex bg-white rounded-md shadow-sm hover:shadow-md transition-shadow"
            >
              <div class="w-16 h-16 flex-shrink-0">
                <img
                  :src="`${config.public.backendCdnUrl}${relatedArticle.thumbnailUrl}`"
                  class="w-full h-full rounded-l-md object-cover"
                  :alt="relatedArticle.title"
                >
              </div>
              <div class="flex-1 px-4 py-2">
                <span class="font-medium text-poivre-800 hover:text-poivre-600 line-clamp-2 text-sm">
                  {{ relatedArticle.title }}
                </span>
              </div>
            </NuxtLink>
          </div>
          <div v-else class="text-poivre-500 text-sm">
            Aucune suggestion associée pour l'instant.
          </div>

          <!-- Recent Articles -->
          <div class="pb-2 mt-5 mb-2 border-b border-poivre-200">
            <h3 class="text-2xl leading-6 text-poivre-800 font-title">
              Articles récents
            </h3>
          </div>

          <div v-if="recentArticles.length > 0" class="space-y-3">
            <NuxtLink
              v-for="recentArticle in recentArticles"
              :key="recentArticle.id"
              :to="$localePath({ name: 'ArticlePage', params: { id: recentArticle.id, slug: recentArticle.slug } })"
              class="flex bg-white rounded-md shadow-sm hover:shadow-md transition-shadow"
            >
              <div class="w-16 h-16 flex-shrink-0">
                <img
                  :src="`${config.public.backendCdnUrl}${recentArticle.thumbnailUrl}`"
                  class="w-full h-full rounded-l-md object-cover"
                  :alt="recentArticle.title"
                >
              </div>
              <div class="flex-1 px-4 py-2">
                <span class="font-medium text-poivre-800 hover:text-poivre-600 line-clamp-2 text-sm">
                  {{ recentArticle.title }}
                </span>
              </div>
            </NuxtLink>
          </div>
          <div v-else class="text-poivre-500 text-sm">
            Aucun article récent.
          </div>
        </div>
      </div>

      <!-- Article not found -->
      <div v-else class="text-center py-12">
        <UIcon name="i-mdi-file-document-outline" class="text-4xl text-poivre-400 mb-4" />
        <h2 class="text-xl font-semibold text-poivre-800 mb-2">
          Article non trouvé
        </h2>
        <p class="text-poivre-600">
          L'article que vous recherchez n'existe pas ou a été supprimé.
        </p>
      </div>
    </UContainer>
  </div>
</template>

<style scoped>
.single-article :deep(ol) {
  line-height: 1.3em;
  counter-reset: ing-counter;
  list-style-position: outside;
  margin-top: 0px;
  margin-bottom: 15px;
  margin-left: 35px;
  padding-left: 0;
  counter-reset: item;
}

.single-article :deep(ul) {
  line-height: 1.3em;
  counter-reset: ing-counter;
  list-style-position: outside;
  margin-top: 0px;
  margin-bottom: 15px;
  margin-left: 35px;
  padding-left: 0;
  counter-reset: item;
}

.single-article :deep(a) {
  color: #dc2626;
  text-decoration: underline;
}

.single-article :deep(ol > li) {
  line-height: 1.3em;
  margin: 0 0 10px 0;
  padding: 0 0 0 0;
  list-style-type: none;
  counter-increment: item;
}

.single-article :deep(ol > li:before) {
  display: inline-block;
  text-indent: -1.5em;
  font-weight: 500;
  text-align: left;
  color: #1e3a8a;
  content: counter(item) '.';
}

.single-article :deep(ol > li:last-child) {
  margin: 0 !important;
}

.single-article :deep(ul > li) {
  line-height: 1.3em;
  margin: 0 0 10px 0;
  padding: 0 0 0 0;
  list-style-type: none;
  counter-increment: item;
}

.single-article :deep(ul > li:before) {
  display: inline-block;
  text-indent: -1.3em;
  font-weight: 500;
  text-align: left;
  color: #1e3a8a;
  content: '-';
}

.single-article :deep(ul > li:last-child) {
  margin: 0 !important;
}

.single-article :deep(p) {
  margin-bottom: 15px;
}

.single-article :deep(h1),
.single-article :deep(h2),
.single-article :deep(h3),
.single-article :deep(h4),
.single-article :deep(h5),
.single-article :deep(h6) {
  font-weight: bold;
  margin-bottom: 10px;
  margin-top: 20px;
  color: #1f2937;
  font-family: 'Albra Bold', sans-serif;
}

.single-article :deep(h2) {
  font-size: 1.875rem;
  line-height: 2.25rem;
}

.single-article :deep(h3) {
  font-size: 1.5rem;
  line-height: 2rem;
}

.single-article :deep(h4),
.single-article :deep(h5) {
  font-size: 1.25rem;
  line-height: 1.75rem;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
